import { useRef, useState, useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';
import BoundingBoxLayer from './BoundingBoxLayer';
import ViewerToolbar from './ViewerToolbar';
import { viewerZoomAtom, visibleLayersAtom } from '@/hooks/atoms';
import type { Finding } from '@/types/forensics';

interface ImageViewerWithOverlayProps {
  findings: Finding[];
  imageWidth?: number;
  imageHeight?: number;
  previewUrl?: string | null;
}

export default function ImageViewerWithOverlay({
  findings,
  imageWidth = 1920,
  imageHeight = 1280,
  previewUrl,
}: ImageViewerWithOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [zoom, setZoom] = useAtom(viewerZoomAtom);
  const [layers, setLayers] = useAtom(visibleLayersAtom);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: imageWidth, height: imageHeight });

  const showBoxes = layers.has('bounding_boxes');

  // Track container size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // When image loads, get its real natural dimensions
  const handleImageLoad = useCallback(() => {
    const img = imgRef.current;
    if (img) {
      setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    }
  }, []);

  // Compute the actual rendered image size (fit within container, preserving aspect ratio)
  const aspect = naturalSize.width / naturalSize.height;
  let renderedWidth = containerSize.width;
  let renderedHeight = containerSize.width / aspect;

  if (renderedHeight > containerSize.height) {
    renderedHeight = containerSize.height;
    renderedWidth = containerSize.height * aspect;
  }

  // Apply zoom
  const displayWidth = renderedWidth * zoom;
  const displayHeight = renderedHeight * zoom;

  const handleZoomIn = useCallback(() => setZoom((z) => Math.min(z + 0.25, 4)), [setZoom]);
  const handleZoomOut = useCallback(() => setZoom((z) => Math.max(z - 0.25, 0.25)), [setZoom]);
  const handleFit = useCallback(() => setZoom(1), [setZoom]);

  const handleToggleBoxes = useCallback(() => {
    setLayers((prev) => {
      const next = new Set(prev);
      if (next.has('bounding_boxes')) {
        next.delete('bounding_boxes');
      } else {
        next.add('bounding_boxes');
      }
      return next;
    });
  }, [setLayers]);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom((z) => Math.max(0.25, Math.min(4, z + delta)));
      }
    },
    [setZoom],
  );

  return (
    <div className="relative flex flex-col h-full">
      {/* Viewer area */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-muted/30 flex items-center justify-center" onWheel={handleWheel}>
        {/* Image + overlay wrapper — sized exactly to the rendered image */}
        <div className="relative" style={{ width: displayWidth, height: displayHeight }}>
          {previewUrl ? (
            <img
              ref={imgRef}
              src={previewUrl}
              alt="Analysis subject"
              className="block w-full h-full"
              style={{ objectFit: 'fill' }}
              draggable={false}
              onLoad={handleImageLoad}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
              <div className="text-center text-slate-500">
                <p className="text-sm font-medium">Image Preview</p>
                <p className="text-xs">
                  {imageWidth} x {imageHeight}
                </p>
              </div>
            </div>
          )}

          {/* Bounding box overlay — same size as the rendered image */}
          {showBoxes && <BoundingBoxLayer findings={findings} containerWidth={displayWidth} containerHeight={displayHeight} />}
        </div>
      </div>

      {/* Toolbar */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10">
        <ViewerToolbar
          zoom={zoom}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFit={handleFit}
          showBoxes={showBoxes}
          onToggleBoxes={handleToggleBoxes}
        />
      </div>
    </div>
  );
}
